import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownRight,
  ArrowLeft,
  ArrowRight,
  Atom,
  Box,
  BrainCircuit,
  BriefcaseBusiness,
  Check,
  ChevronRight,
  CircleDot,
  Cpu,
  ExternalLink,
  Glasses,
  MapPin,
  Menu,
  MoonStar,
  NotebookPen,
  Radio,
  ScanLine,
  Sun,
  Wrench,
  X,
} from 'lucide-react';
import { Link, NavLink, Route, Routes, useLocation, useParams } from 'react-router-dom';

const fallbackState = {
  updatedAt: '2026-08-21',
  now: {
    headline: 'Testing the distance between a good demo and a useful habit.',
    threads: [
      'Practical AI literacy — understanding the system, not memorising the vocabulary',
      'Spatial computing as a real workspace instead of a showroom trick',
      'Small bilingual field notes about technology in everyday life',
    ],
  },
  notes: [
    {
      slug: 'understanding-ai-not-vocabulary',
      date: '2026-08-10',
      tag: 'AI literacy',
      title: 'Knowing the acronym is not knowing the system',
      dek: 'Agents, MCP, RAG and workflows sound familiar fast. Understanding begins when you can trace what connects to what, where it can fail, and who checks the answer.',
      xUrl: 'https://x.com/abood_akkad/status/2086888134462357791',
      image: '/images/field-ai-literacy.jpg',
    },
    {
      slug: 'ask-maps-one-good-question',
      date: '2026-08-12',
      tag: 'Useful AI',
      title: 'One good question instead of ten open tabs',
      dek: 'A practical Maps test: find a highly rated coffee shop with Wi-Fi and easy access in an unfamiliar neighbourhood — without doing the comparison by hand.',
      xUrl: 'https://x.com/abood_akkad/status/2087580250842784196',
      image: '/images/field-maps.jpg',
    },
    {
      slug: 'mirror-logic-test',
      date: '2026-08-12',
      tag: 'Generative media',
      title: 'A mirror is a better AI test than another pretty portrait',
      dek: 'The interesting part of an edit is not the muscles. It is whether the model preserves physical logic when the subject also exists as a reflection.',
      xUrl: 'https://x.com/abood_akkad/status/2087593735219790103',
      image: '/images/field-mirror.jpg',
    },
    {
      slug: 'morning-briefing-not-doomscrolling',
      date: '2026-08-13',
      tag: 'Personal systems',
      title: 'Same phone. Better first ten minutes.',
      dek: 'Instead of opening the day with a feed, an AI agent prepares a concise morning briefing around the topics I actually care about.',
      xUrl: 'https://x.com/abood_akkad/status/2087955490198393189',
      image: '/images/field-newsletter.jpg',
    },
  ],
};

const interests = [
  {
    key: 'ai',
    number: '01',
    icon: BrainCircuit,
    title: 'AI, without the theatre',
    copy: 'I care less about the launch-day demo and more about what survives contact with a normal Tuesday.',
    accent: 'amber',
  },
  {
    key: 'places',
    number: '02',
    icon: MapPin,
    title: 'Why places work',
    copy: 'F&B leasing trained me to notice the quiet system behind a good place: fit, timing, people, and the reason to return.',
    accent: 'sage',
  },
  {
    key: 'devices',
    number: '03',
    icon: Cpu,
    title: 'Devices with a job',
    copy: 'Phones, PCs, spatial computing, cameras. The question is always the same: what does this make meaningfully better?',
    accent: 'blue',
  },
  {
    key: 'making',
    number: '04',
    icon: Box,
    title: 'Making things real',
    copy: 'A prototype, a printed object, a small automation — finished proof teaches more than another saved tutorial.',
    accent: 'rose',
  },
  {
    key: 'stories',
    number: '05',
    icon: MoonStar,
    title: 'Stories & anime',
    copy: 'I like worlds with rules, visible progression, and characters who earn the next version of themselves.',
    accent: 'violet',
  },
];

const experiments = [
  {
    status: 'Active',
    index: 'E–01',
    title: 'Can AI replace the morning feed?',
    copy: 'A daily briefing built around actual interests instead of whatever an engagement algorithm chooses first.',
    measure: 'Calmer start · useful signal · less scroll',
    icon: Radio,
    href: '/note/morning-briefing-not-doomscrolling',
  },
  {
    status: 'Testing',
    index: 'E–02',
    title: 'A virtual desk before another real monitor',
    copy: 'Using spatial computing as a flexible workspace, then judging it by comfort, friction and repeat use — not novelty.',
    measure: 'Focus · ergonomics · no-buy decision',
    icon: Glasses,
  },
  {
    status: 'Ongoing',
    index: 'E–03',
    title: 'From power user to useful builder',
    copy: 'Small products, workflows and field notes as a way to learn the full distance between an idea and a reliable result.',
    measure: 'Finished proof · clear explanation · feedback',
    icon: Wrench,
  },
  {
    status: 'Field note',
    index: 'E–04',
    title: 'Does the model understand the scene?',
    copy: 'Testing generative media with reflections, continuity and physical logic instead of scoring it on polish alone.',
    measure: 'Consistency · causality · visible failure',
    icon: ScanLine,
    href: '/note/mirror-logic-test',
  },
];

const hardwareItems = [
  {
    number: '01',
    category: 'Compute',
    name: 'Ryzen 9 9950X3D + MSI GeForce RTX™ 5090 32G VENTUS 3X OC',
    label: 'Windows AI & gaming workstation',
    copy: 'The local muscle for AI experiments, high-end gaming and compute-heavy creative work.',
    image: '/images/hardware/workstation-msi-ventus.webp',
    tone: 'nvidia',
    size: 'wide',
    sources: [
      ['AMD', 'https://www.amd.com/en/products/processors/desktops/ryzen/9000-series/amd-ryzen-9-9950x3d.html'],
      ['MSI', 'https://www.msi.com/Graphics-Card/GeForce-RTX-5090-32G-VENTUS-3X-OC'],
    ],
  },
  {
    number: '02',
    category: 'Compute',
    name: 'Mac mini M4 Pro',
    label: 'The compact AI hub',
    copy: 'The small Mac that keeps research, local AI work and everyday building close at hand.',
    image: '/images/hardware/mac-mini.jpg',
    tone: 'paper',
    fit: 'cover',
    sources: [['Apple', 'https://www.apple.com/mac-mini/']],
  },
  {
    number: '03',
    category: 'Portable',
    name: 'MacBook Pro 14″ M4 Pro',
    label: 'Work that can leave the desk',
    copy: 'The portable Mac for travel, deep work and continuing a build away from the main setup.',
    image: '/images/hardware/macbook-pro.jpg',
    tone: 'midnight',
    sources: [['Apple', 'https://support.apple.com/en-us/121553']],
  },
  {
    number: '04',
    category: 'Display',
    name: 'LG UltraGear 45GX950A-B',
    label: '45-inch 5K2K OLED',
    copy: 'The primary panoramic canvas: dense enough for work, fast enough for play, and large enough to disappear into.',
    image: '/images/hardware/lg-ultragear-front-cutout.png',
    tone: 'ultraviolet',
    size: 'wide',
    fit: 'lg',
    sources: [['LG', 'https://www.lg.com/us/monitors/lg-45gx950a-b-gaming-monitor']],
  },
  {
    number: '17',
    category: 'Display',
    name: 'CORSAIR XENEON EDGE 14.5″ LCD Touchscreen Display',
    label: '2560×720 · ultrawide touch',
    copy: 'The slim touch display for widgets, system information, or a secondary monitor—an interactive strip across the desk setup.',
    image: '/images/hardware/corsair-xeneon-edge.png',
    tone: 'studio-blue',
    size: 'wide',
    fit: 'contain',
    sources: [['CORSAIR', 'https://www.corsair.com/us/en/p/monitors/cc-9011306-ww/xeneon-edge-14-5-lcd-touchscreen-cc-9011306-ww']],
  },
  {
    number: '05',
    category: 'Spatial',
    name: 'Apple Vision Pro (M5)',
    label: 'A workspace without another screen',
    copy: 'Spatial computing for virtual workspace, media and first-person technology experiments.',
    image: '/images/hardware/vision-pro.jpg',
    tone: 'ice',
    sources: [['Apple', 'https://www.apple.com/apple-vision-pro/']],
  },
  {
    number: '06',
    category: 'Mobile',
    name: 'OPPO Find X9 Ultra',
    label: 'Daily Android · camera first',
    copy: 'The everyday phone and the quickest route from an idea to a strong mobile image.',
    image: '/images/hardware/oppo-find-x9-ultra.png',
    tone: 'umber',
    fit: 'contain',
    sources: [['OPPO', 'https://www.oppo.com/en/smartphones/series-find-x/find-x9-ultra']],
  },
  {
    number: '07',
    category: 'Mobile',
    name: 'iPhone 17 Pro Max · Deep Blue',
    label: 'Deep Blue · the iOS side of the lab',
    copy: 'An iOS test surface and a second creator camera when one angle is not enough.',
    image: '/images/hardware/iphone-17-pro-max-deep-blue.jpg',
    tone: 'deep-blue',
    fit: 'contain',
    sources: [['Apple', 'https://www.apple.com/iphone-17-pro/']],
  },
  {
    number: '08',
    category: 'Portable',
    name: 'iPad Pro 13″ M5',
    label: 'The portable canvas',
    copy: 'A large, thin surface for reading, review, visual work and mobile control.',
    image: '/images/hardware/ipad-pro-m5.jpg',
    tone: 'caldera',
    sources: [['Apple', 'https://support.apple.com/en-us/125407']],
  },
  {
    number: '09',
    category: 'Capture',
    name: 'Sony α7 II',
    label: 'Full-frame photography',
    copy: 'The deliberate camera for stills and shoots that deserve more control than a phone.',
    image: '/images/hardware/sony-a7ii.jpg',
    tone: 'studio',
    fit: 'contain',
    sources: [['Sony', 'https://www.sony.com/electronics/support/e-mount-body-ilce-7-series/ilce-7m2']],
  },
  {
    number: '10',
    category: 'Capture',
    name: 'Insta360 Link 2',
    label: '4K gimbal webcam',
    copy: 'Tracked desktop framing for talking-head video, calls and hands-on demonstrations.',
    image: '/images/hardware/insta360-link2.png',
    tone: 'mint',
    fit: 'contain',
    sources: [['Insta360', 'https://www.insta360.com/product/insta360-link2']],
  },
  {
    number: '11',
    category: 'Capture',
    name: 'Insta360 Flow 2 Pro',
    label: 'Phone gimbal · AI tracking',
    copy: 'Stabilized movement and solo subject tracking without turning a quick shoot into a production crew.',
    image: '/images/hardware/insta360-flow2-pro.jpg',
    tone: 'white',
    sources: [['Insta360', 'https://www.insta360.com/blog/news/insta360-releases-flow-2-pro.html']],
  },
  {
    number: '12',
    category: 'Audio',
    name: 'Hollyland LARK MAX 2',
    label: 'Wireless creator audio',
    copy: 'Clean voice capture for explainers, product tests and two-person recording.',
    image: '/images/hardware/lark-max2.png',
    tone: 'graphite',
    fit: 'contain',
    sources: [['Hollyland', 'https://www.hollyland.com/product/lark-max2']],
  },
  {
    number: '13',
    category: 'Control',
    name: 'Elgato Stream Deck+',
    label: 'Tactile workflow control',
    copy: 'Buttons and dials for recording, shortcuts and live workflows that should not require another menu.',
    image: '/images/hardware/stream-deck-plus.jpg',
    tone: 'studio-blue',
    sources: [['Elgato', 'https://elgato.com/us/en/p/stream-deck-plus']],
  },
  {
    number: '14',
    category: 'Desk',
    name: 'Secretlab MAGNUS Pro XL',
    foundation: true,
    label: '1.7m sit-to-stand metal desk',
    copy: 'The physical foundation: a wide, height-adjustable surface designed to carry serious work without looking like office furniture.',
    image: '/images/hardware/secretlab-magnus-pro-xl.webp',
    tone: 'secretlab',
    size: 'wide',
    sources: [['Secretlab', 'https://secretlab.co/pages/magnus-pro']],
  },
  {
    number: '15',
    category: 'Connectivity',
    name: 'CalDigit Thunderbolt Station 5 (TS5)',
    label: 'Thunderbolt 5 dock',
    copy: 'One compact connection point for displays, accessories and fast device changes across the desk.',
    image: '/images/hardware/caldigit-ts5.jpg',
    tone: 'caldigit',
    sources: [['CalDigit', 'https://www.caldigit.com/introducing-the-caldigit-ts5-ts5-plus/']],
  },
  {
    number: '16',
    category: 'Storage',
    name: 'UGREEN NASync DXP4800 Pro',
    label: 'Private storage appliance · model only',
    copy: 'The private storage and backup appliance in the setup. This public card stops at model and role.',
    image: '/images/hardware/ugreen-dxp4800-pro.webp',
    tone: 'silver',
    fit: 'contain',
    sources: [['UGREEN', 'https://ai.ugreen.com/products/ugreen-nasync-dxp4800-pro-136tb-4-bay-nas']],
  },
  {
    number: '18',
    category: 'Input',
    name: 'Lofree Hyzen',
    label: 'Mechanical-magnetic keyboard',
    copy: 'The keyboard on my desk: a tactile part of the setup for writing, building and everyday work.',
    image: '/images/hardware/lofree-hyzen.png',
    tone: 'silver',
    fit: 'contain',
    sources: [['Lofree', 'https://www.lofree.co/pages/hyzen']],
  },
];

const edcItems = [
  {
    number: '03', category: 'Canvas', name: 'iPad Pro 13″ (M5)', label: 'The portable center of gravity',
    copy: 'The large mobile canvas for reading, reviewing, sketching, steering work and carrying context away from the desk.',
    image: '/images/edc/ipad-pro-13-m5.jpg', size: 'wide', tone: 'caldera',
    sources: [['Apple', 'https://www.apple.com/ipad-pro/']],
  },
  {
    number: '04', category: 'Input', name: 'Apple Pencil Pro', label: 'Current Pro model',
    copy: 'The current Pencil for the M5 iPad Pro: notes, markup and direct-on-canvas thinking.',
    image: '/images/edc/apple-pencil-pro.jpg', tone: 'paper',
    sources: [['Apple', 'https://www.apple.com/apple-pencil/']],
  },
  {
    number: '05', category: 'iPad carry', name: 'MOFT Dynamic Folio for iPad Pro 13″', label: 'Folio · multi-angle stand',
    copy: 'Protection that earns its bulk by turning the iPad into a flexible reading, writing and viewing surface.',
    image: '/images/edc/moft-dynamic-folio.webp', tone: 'paper',
    sources: [['MOFT', 'https://www.moft.com/products/dynamic-folio-case']],
  },
  {
    number: '06', category: 'Computer', name: 'MacBook Pro 14″ M4 Pro', label: 'Portable full-work machine',
    copy: 'Abood’s M4 Pro MacBook for full builds and deep work when the iPad is not enough.',
    image: '/images/hardware/macbook-pro.jpg', size: 'wide', tone: 'midnight',
    sources: [['Apple', 'https://support.apple.com/en-us/121553']],
  },
  {
    number: '07', category: 'Phone utility', name: 'AULUMU G09 Omnidirectional Dual-Sided Phone Stand', label: 'G09 · three-axis magnetic stand',
    copy: 'A compact magnetic grip and stand that can change orientation without asking for another accessory.',
    image: '/images/edc/aulumu-g09.webp', tone: 'graphite',
    sources: [['AULUMU', 'https://aulumu.com/products/g09-infinite-360-magsafe-stand']],
  },
  {
    number: '08', category: 'Phone utility', name: 'MOFT Snap Phone Tripod Wallet (MS027W)', label: 'Wallet + tripod',
    copy: 'A MagSafe wallet that unfolds into a quick tripod for framing, calls and spontaneous capture.',
    image: '/images/edc/moft-snap-tripod.webp', tone: 'paper',
    sources: [['MOFT', 'https://www.moft.com/products/invisible-phone-tripod-wallet']],
  },
  {
    number: '09', category: 'Power', name: 'Anker Prime Power Bank (20K, 220W) · black', label: '20,100mAh · 220W',
    copy: 'The black 20K/220W model: portable power sized for a laptop, tablet, phone and a long multi-device day.',
    image: '/images/edc/anker-prime-20k-220w.webp', tone: 'graphite',
    sources: [['Anker', 'https://www.anker.com/products/a110b-anker-prime-20k-220w-power-bank']],
  },
  {
    number: '01', category: 'Carry', name: 'tomtoc TSA-friendly 32L laptop backpack', label: 'UrbanEX-T77 · 32L',
    copy: 'The main carry: a structured 32-liter laptop backpack with a TSA-friendly opening for devices and travel essentials.',
    image: '/images/edc/tomtoc-urbanex-t77-32l.jpg', size: 'wide', tone: 'paper',
    sources: [['tomtoc', 'https://www.tomtoc.com/products/urbanex-t77-laptop-backpack-32l']],
  },
  {
    number: '02', category: 'Organization', name: 'tomtoc Navigator-T13 accessory pouch · 2.4L', label: 'Medium · 2.4L',
    copy: 'A dedicated 2.4-liter home for chargers, cables and the small objects that otherwise disappear into the backpack.',
    image: '/images/edc/tomtoc-navigator-t13-2-4l.webp', tone: 'paper',
    sources: [['tomtoc', 'https://www.tomtoc.com/products/accordion-t13-accessory-pouch']],
  },
  {
    number: '10', category: 'Analog', name: 'Unbranded paper notepad', label: 'Paper first · no invented brand',
    copy: 'The deliberately ordinary piece: fast capture, rough thinking and a surface that never needs charging.',
    size: 'half', tone: 'notepad', notepad: true, sources: [],
  },
  {
    number: '11', category: 'Writing', name: 'Uni-ball ZENTO Signature Model · Metallic Black · 0.5 mm · UBZH05M.24', label: 'Black ink · stylus/touch',
    copy: 'The exact metallic-black Signature Model with ZENTO ink and the original touch-pen function—never a generic pen substitute.',
    image: '/images/edc/zento-ubzh05m24.webp', size: 'half', tone: 'paper',
    sources: [
      ['Model', 'https://www.amazon.com/ZENTO-Signature-Metallic-Ballpoint-UBZH05M-24/dp/B0DYNZ4T7J'],
      ['Image', 'https://wafuu.com/en-us/products/mitsubishi-pencil-uni-ball-zento-signature-model-pen-05mm-metallic-black'],
    ],
  },
];

const stackItems = [
  {
    number: '01', name: 'Notion', size: 'wide', logo: '/images/stack/notion.png',
    description: 'Where I organize projects, actions, and the parts of life that need a clear next step.',
    source: 'https://www.notion.com/',
  },
  {
    number: '02', name: 'Obsidian', logo: '/images/stack/obsidian.svg',
    description: 'My durable knowledge base—the place context, links, and decisions stay connected over time.',
    source: 'https://obsidian.md/',
  },
  {
    number: '03', name: 'OpenAI Codex', logo: '/images/stack/openai-codex-wordmark.png', logoClass: 'wordmark',
    description: 'The coding agent I use to turn specifications into working, tested software.',
    source: 'https://openai.com/codex/',
  },
  {
    number: '04', name: 'Google Gemini', logo: '/images/stack/google-gemini.svg',
    description: 'My multimodal workspace for long context, files, images, and a second analytical view.',
    source: 'https://gemini.google.com/',
  },
  {
    number: '05', name: 'xAI Grok', logo: '/images/stack/xai-grok.svg',
    description: 'My live lens on X, current conversations, and ideas moving at internet speed.',
    source: 'https://grok.com/',
  },
  {
    number: '06', name: 'Wispr Flow', size: 'half', logo: '/images/stack/wispr-flow.png',
    description: 'The voice layer that turns rough spoken thoughts into clean text before the moment disappears.',
    source: 'https://wisprflow.ai/',
  },
  {
    number: '07', name: 'Tailscale', size: 'half', logo: '/images/stack/tailscale.png',
    description: 'Secure access between my machines without exposing how the network is arranged.',
    source: 'https://tailscale.com/',
  },
];

function useSiteState() {
  const [state, setState] = useState(fallbackState);
  useEffect(() => {
    if (import.meta.env.VITE_STANDALONE === 'true') return undefined;
    let active = true;
    fetch('/api/site-state')
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('API unavailable'))))
      .then((data) => active && setState(data))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  return state;
}

function usePageMeta(title, description, image = '/images/abood-hero.png') {
  useEffect(() => {
    const fullTitle = title === 'Home' ? 'Abood Akkad — Curious by practice' : `${title} — Abood Akkad`;
    document.title = fullTitle;

    const setMeta = (selector, attribute, value) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        const [key, name] = attribute;
        element.setAttribute(key, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', value);
    };

    setMeta('meta[name="description"]', ['name', 'description'], description);
    setMeta('meta[property="og:title"]', ['property', 'og:title'], fullTitle);
    setMeta('meta[property="og:description"]', ['property', 'og:description'], description);
    const imageUrl = image.startsWith('data:') || window.location.protocol === 'file:'
      ? image
      : new URL(image, window.location.origin).href;
    setMeta('meta[property="og:image"]', ['property', 'og:image'], imageUrl);
  }, [title, description, image]);
}

const THEME_KEY = 'abood-akkad-theme';

function preferredTheme() {
  try {
    const saved = window.localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {}
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function useTheme() {
  const [theme, setTheme] = useState(preferredTheme);

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    try {
      window.localStorage.setItem(THEME_KEY, theme);
    } catch {}
  }, [theme]);

  return {
    theme,
    toggleTheme: () => setTheme((current) => (current === 'dark' ? 'light' : 'dark')),
  };
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function CursorGlow() {
  const glowRef = useRef(null);
  useEffect(() => {
    const move = (event) => {
      if (!glowRef.current) return;
      glowRef.current.style.setProperty('--x', `${event.clientX}px`);
      glowRef.current.style.setProperty('--y', `${event.clientY}px`);
    };
    window.addEventListener('pointermove', move, { passive: true });
    return () => window.removeEventListener('pointermove', move);
  }, []);
  return <div className="cursor-glow" ref={glowRef} aria-hidden="true" />;
}

function Logo() {
  return (
    <Link to="/" className="logo" aria-label="Abood Akkad — home">
      <span className="logo-mark">AA</span>
      <span className="logo-name">
        Abood Akkad
        <small>Dubai · 25.2048° N</small>
      </span>
    </Link>
  );
}

function ThemeToggle({ theme, onToggle }) {
  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      className="theme-toggle"
      type="button"
      onClick={onToggle}
      aria-label={`Switch to ${nextTheme} mode`}
      title={`Switch to ${nextTheme} mode`}
    >
      {theme === 'dark' ? <Sun size={15} /> : <MoonStar size={15} />}
      <span>{nextTheme}</span>
    </button>
  );
}

function Header({ theme, onToggleTheme }) {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 860px)');
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const links = [
    ['About', '/about'],
    ['Field notes', '/field-notes'],
    ['Experiments', '/experiments'],
    ['Setup', '/setup'],
    ['Now', '/now'],
    ['EDC', '/edc'],
    ['Stack', '/stack'],
  ];
  const navClosed = isMobile && !open;

  return (
    <header className="site-header">
      <div className="header-inner">
        <Logo />
        <nav
          id="primary-navigation"
          className={`main-nav ${open ? 'is-open' : ''}`}
          aria-label="Primary navigation"
          aria-hidden={navClosed}
          inert={navClosed}
        >
          {links.map(([label, to]) => (
            <NavLink key={to} to={to} className={({ isActive }) => (isActive ? 'active' : '')}>
              {label}
            </NavLink>
          ))}
          <a className="nav-social" href="https://x.com/abood_akkad" target="_blank" rel="noreferrer">
            X <ExternalLink size={12} />
          </a>
        </nav>
        <div className="header-controls">
          <button className="menu-button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls="primary-navigation" aria-label="Toggle menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <ThemeToggle theme={theme} onToggle={onToggleTheme} />
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-top">
        <div>
          <p className="kicker">Open channel</p>
          <h2>Have a tool, device or idea<br />worth testing properly?</h2>
        </div>
        <a className="circle-link" href="https://x.com/abood_akkad" target="_blank" rel="noreferrer" aria-label="Find Abood on X">
          <ArrowDownRight />
        </a>
      </div>
      <div className="footer-bottom">
        <Logo />
        <p>Syrian by origin. Dubai by practice.<br />Curious everywhere.</p>
        <div className="footer-links">
          <a href="https://x.com/abood_akkad" target="_blank" rel="noreferrer">X / Twitter</a>
          <a href="https://www.linkedin.com/in/abdul-majeed-akkad" target="_blank" rel="noreferrer">LinkedIn</a>
        </div>
        <p className="copyright">© 2026 Abood Akkad<br />Built to stay curious.</p>
      </div>
    </footer>
  );
}

function PageShell({ children }) {
  const { theme, toggleTheme } = useTheme();
  return (
    <div className="site-shell">
      <CursorGlow />
      <div className="grain" aria-hidden="true" />
      <Header theme={theme} onToggleTheme={toggleTheme} />
      <main>{children}</main>
      <Footer />
    </div>
  );
}

function CuriosityEngine() {
  const [active, setActive] = useState(interests[0]);
  const frameRef = useRef(null);

  const move = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    frameRef.current?.style.setProperty('--rx', `${y * -7}deg`);
    frameRef.current?.style.setProperty('--ry', `${x * 7}deg`);
  };

  const reset = () => {
    frameRef.current?.style.setProperty('--rx', '0deg');
    frameRef.current?.style.setProperty('--ry', '0deg');
  };

  return (
    <div className="engine-wrap" onPointerMove={move} onPointerLeave={reset}>
      <div className="engine" ref={frameRef}>
        <div className="engine-grid" />
        <div className="orbit orbit-one" />
        <div className="orbit orbit-two" />
        <div className="orbit orbit-three" />
        <div className="scan-beam" />
        <span className="coord coord-a">DXB / 25.2048</span>
        <span className="coord coord-b">CURIOUS / 05</span>
        <span className="coord coord-c">LIVE SIGNAL</span>
        <div className="engine-core">
          <img src="/images/abood-hero.png" alt="Abood Akkad writing beside his Mac" />
          <span className="core-ring" />
        </div>
        {interests.map((item, index) => (
          <button
            key={item.key}
            className={`orbit-node node-${index + 1} ${active.key === item.key ? 'active' : ''}`}
            onPointerEnter={() => setActive(item)}
            onFocus={() => setActive(item)}
            onClick={() => setActive(item)}
          >
            <span>{item.number}</span>
            {item.key}
          </button>
        ))}
        <div className="engine-readout">
          <span>{active.number} / live lens</span>
          <strong>{active.title}</strong>
          <p>{active.copy}</p>
        </div>
      </div>
    </div>
  );
}

function SignalStrip() {
  return (
    <div className="signal-strip" aria-label="Current status">
      <span className="signal-label"><i /> Live / Now</span>
      <div className="signal-marquee">
        <div>
          <span>Testing useful AI, not just impressive AI</span><b>✦</b>
          <span>Learning in public, in English and Arabic</span><b>✦</b>
          <span>Watching where technology meets actual behaviour</span><b>✦</b>
          <span>Testing useful AI, not just impressive AI</span><b>✦</b>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({ kicker, title, aside }) {
  return (
    <div className="section-heading">
      <div>
        <p className="kicker">{kicker}</p>
        <h2>{title}</h2>
      </div>
      {aside && <p className="section-aside">{aside}</p>}
    </div>
  );
}

function NoteCard({ note, featured = false }) {
  return (
    <article className={`note-card ${featured ? 'featured' : ''}`}>
      <Link to={`/note/${note.slug}`} className="note-image" aria-label={`Read ${note.title}`}>
        <img src={note.image} alt="" />
        <span className="image-wash" />
        <span className="note-index">FIELD / {note.date.slice(5).replace('-', '.')}</span>
        <span className="note-arrow"><ArrowUpRightIcon /></span>
      </Link>
      <div className="note-body">
        <div className="note-meta"><span>{note.tag}</span><time>{formatDate(note.date)}</time></div>
        <h3><Link to={`/note/${note.slug}`}>{note.title}</Link></h3>
        <p>{note.dek}</p>
      </div>
    </article>
  );
}

function ArrowUpRightIcon() {
  return <ArrowDownRight style={{ transform: 'rotate(-90deg)' }} />;
}

function formatDate(dateString) {
  return new Intl.DateTimeFormat('en', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(`${dateString}T00:00:00`));
}

function Home() {
  const state = useSiteState();
  usePageMeta('Home', 'Abood Akkad is a Syrian in Dubai exploring useful AI, technology, places, gadgets, making and anime through real-world tests.');
  return (
    <>
      <section className="hero section-pad">
        <div className="hero-copy">
          <p className="kicker reveal">Syrian in Dubai · Curious by practice</p>
          <h1 className="reveal delay-1">I look at what’s <em>next</em> — then test whether it belongs in real life.</h1>
          <p className="hero-lede reveal delay-2">
            I’m Abood. I work where restaurants, places and people meet. Off-hours, I pull apart AI tools, devices and ideas until I can explain what actually matters.
          </p>
          <div className="hero-actions reveal delay-3">
            <Link className="button button-solid" to="/field-notes">Read the field notes <ArrowRight size={16} /></Link>
            <Link className="text-link" to="/about">More about me <ChevronRight size={15} /></Link>
          </div>
          <div className="hero-footnote reveal delay-3">
            <span>01 — 05</span>
            <p>AI · Places · Devices<br />Making · Stories</p>
          </div>
        </div>
        <CuriosityEngine />
      </section>

      <SignalStrip />

      <section className="lenses section-pad">
        <SectionHeading
          kicker="Five lenses"
          title={<>Not a niche.<br /><em>A way of noticing.</em></>}
          aside="The topics move. The method stays: get curious, go deeper than the headline, try the thing, return with an honest opinion."
        />
        <div className="lens-grid">
          {interests.map((item) => {
            const Icon = item.icon;
            return (
              <article className={`lens-card accent-${item.accent}`} key={item.key}>
                <div className="lens-top"><span>{item.number}</span><Icon size={21} /></div>
                <div><h3>{item.title}</h3><p>{item.copy}</p></div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="dispatches section-pad section-rule">
        <SectionHeading
          kicker="Recent dispatches"
          title={<>Notes from contact<br /><em>with the real thing.</em></>}
          aside="Small observations from AI, devices and daily life. Specific enough to test. Honest enough to change later."
        />
        <div className="notes-grid home-notes">
          {state.notes.slice().reverse().slice(0, 3).map((note, index) => <NoteCard note={note} featured={index === 0} key={note.slug} />)}
        </div>
        <div className="section-end"><Link className="button button-outline" to="/field-notes">All field notes <ArrowRight size={16} /></Link></div>
      </section>

      <section className="dual-worlds section-pad section-rule">
        <div className="world world-day">
          <div className="world-number">01</div>
          <div className="world-icon"><BriefcaseBusiness /></div>
          <p className="kicker">By profession</p>
          <h2>The craft of<br /><em>making places work.</em></h2>
          <p>F&B leasing is part judgement, part pattern recognition, part human negotiation. It taught me that the right fit matters more than the loudest name — and that a place only works when the whole system works.</p>
          <Link className="text-link" to="/about">The professional chapter <ArrowRight size={15} /></Link>
        </div>
        <div className="world world-night">
          <div className="world-number">02</div>
          <div className="world-icon"><Atom /></div>
          <p className="kicker">By compulsion</p>
          <h2>The habit of<br /><em>opening the machine.</em></h2>
          <p>I have always been the person who wants to know what is under the interface. AI made that instinct useful: agents, memory, tools and automations are now a working lab, not a spectator sport.</p>
          <Link className="text-link" to="/experiments">Enter the lab <ArrowRight size={15} /></Link>
        </div>
      </section>

      <section className="manifesto section-pad section-rule">
        <div className="manifesto-mark">“</div>
        <blockquote>
          <span className="arabic">باصم ولا فاهم؟</span>
          <p>Are you repeating the words,<br />or do you understand the system?</p>
        </blockquote>
        <div className="manifesto-note">
          <span>Working principle / 2026</span>
          <p>The question I keep returning to — in AI, in work, and in learning.</p>
        </div>
      </section>
    </>
  );
}

function PageHero({ index, kicker, title, copy, children }) {
  return (
    <section className="page-hero section-pad">
      <div className="page-index">{index}</div>
      <div className="page-hero-main">
        <p className="kicker">{kicker}</p>
        <h1>{title}</h1>
      </div>
      <div className="page-hero-side">
        <p>{copy}</p>
        {children}
      </div>
    </section>
  );
}

function About() {
  usePageMeta('About', 'The public story of Abood Akkad: a Syrian in Dubai, F&B leasing professional, technology realist and builder in progress.');
  return (
    <>
      <PageHero
        index="01"
        kicker="A little context"
        title={<>A commercial eye.<br />A geek’s brain.<br /><em>One curious life.</em></>}
        copy="I’m Abdul Majeed Akkad — Abood to almost everyone. Syrian by origin, based in Dubai, and interested in the hidden systems behind places, products and people’s choices."
      />
      <section className="about-portrait section-pad">
        <div className="portrait-panel">
          <img src="/images/abood-about.webp" alt="Close portrait of Abood Akkad against a red background" />
          <span className="portrait-tag">Dubai / 2026</span>
        </div>
        <div className="about-story">
          <p className="kicker">The through-line</p>
          <h2>I’ve spent my career<br /><em>reading fit.</em></h2>
          <p className="story-lede">For fifteen years, I’ve worked across UAE retail and leasing. Today I’m an F&B Leasing Manager at Emaar, focused on the restaurant and café side of major shopping destinations.</p>
          <p>That work is a practical education in judgement. A concept can look perfect on paper and still be wrong for the place. A lesser-known idea can be exactly what the mix needs. You learn to look past the pitch and ask better questions: Who is this for? Why here? Why now? What makes people return?</p>
          <p>I bring the same instinct to technology. I do not want to collect announcements. I want to understand the system, try it in my own life and notice the part the product page left out.</p>
        </div>
      </section>

      <section className="principles section-pad section-rule">
        <SectionHeading kicker="Operating principles" title={<>How I try to<br /><em>approach the work.</em></>} />
        <div className="principle-list">
          {[
            ['01', 'Specific beats impressive', 'A concrete test, a visible trade-off and an honest conclusion are more useful than a grand claim.'],
            ['02', 'Context is part of intelligence', 'The answer changes with the person, the place and the constraint. Good judgement starts there.'],
            ['03', 'Finish something small', 'A working artifact teaches what ten hours of passive learning cannot. Proof compounds.'],
            ['04', 'Stay bilingual in thought', 'I move naturally between English and Arabic. Different ideas sometimes arrive more honestly in different languages.'],
          ].map(([number, title, copy]) => (
            <article key={number}>
              <span>{number}</span><h3>{title}</h3><p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="identity-band section-pad section-rule">
        <div className="identity-word">AKKAD</div>
        <div className="identity-copy">
          <p>Leasing manager</p><i />
          <p>AI power user</p><i />
          <p>Gadget realist</p><i />
          <p>Anime fan</p><i />
          <p>Builder in progress</p>
        </div>
      </section>
    </>
  );
}

function FieldNotes() {
  const state = useSiteState();
  const [filter, setFilter] = useState('All');
  usePageMeta('Field notes', 'Real-world observations about AI, devices and everyday technology from Abood Akkad.');
  const filters = ['All', ...new Set(state.notes.map((note) => note.tag))];
  const notes = filter === 'All' ? state.notes : state.notes.filter((note) => note.tag === filter);
  return (
    <>
      <PageHero
        index="02"
        kicker="Field notes"
        title={<>Small observations.<br /><em>Real contact.</em></>}
        copy="Not a content factory. A growing shelf of tests, distinctions and things I noticed while using technology in real life."
      >
        <div className="field-stats"><span>{String(state.notes.length).padStart(2, '0')} notes</span><span>EN / AR</span></div>
      </PageHero>
      <section className="notes-archive section-pad section-rule">
        <div className="filter-row" aria-label="Filter field notes">
          {filters.map((item) => <button key={item} className={filter === item ? 'active' : ''} onClick={() => setFilter(item)}>{item}</button>)}
        </div>
        <div className="notes-grid archive-grid">
          {notes.slice().reverse().map((note, index) => <NoteCard note={note} featured={index === 0 && filter === 'All'} key={note.slug} />)}
        </div>
      </section>
    </>
  );
}

function Experiments() {
  usePageMeta('Experiments', 'Abood Akkad’s active questions and practical experiments across AI, spatial computing, media and learning by building.');
  return (
    <>
      <PageHero
        index="03"
        kicker="The lab"
        title={<>A demo is a claim.<br /><em>An experiment is a question.</em></>}
        copy="These are the questions currently earning desk space. Each one needs contact with reality, a visible standard and permission to fail."
      />
      <section className="experiment-list section-pad section-rule">
        {experiments.map((experiment) => {
          const Icon = experiment.icon;
          const body = (
            <>
              <div className="experiment-index">{experiment.index}</div>
              <div className="experiment-icon"><Icon /></div>
              <div className="experiment-main"><span className="status"><i />{experiment.status}</span><h2>{experiment.title}</h2><p>{experiment.copy}</p></div>
              <div className="experiment-measure"><span>What I’m watching</span><p>{experiment.measure}</p></div>
              {experiment.href
                ? <div className="experiment-arrow" aria-hidden="true"><ArrowDownRight /></div>
                : <div className="experiment-static">Open<br />question</div>}
            </>
          );
          return experiment.href
            ? <Link className="experiment-row is-link" to={experiment.href} key={experiment.index}>{body}</Link>
            : <article className="experiment-row is-static" key={experiment.index}>{body}</article>;
        })}
      </section>
      <section className="experiment-method section-pad section-rule">
        <SectionHeading kicker="The method" title={<>Curiosity needs<br /><em>a finish line.</em></>} />
        <div className="method-track">
          {[
            ['01', 'Question', 'What would have to be true for this to be useful?'],
            ['02', 'Contact', 'Use it in the real context, not a protected demo.'],
            ['03', 'Friction', 'Notice what breaks, slows down or needs explaining.'],
            ['04', 'Verdict', 'Keep, change or drop — with a reason.'],
          ].map(([number, title, copy]) => <article key={number}><span>{number}</span><CircleDot /><h3>{title}</h3><p>{copy}</p></article>)}
        </div>
      </section>
    </>
  );
}

function Setup() {
  usePageMeta('The setup', 'A curated look at the hardware Abood Akkad actually uses across local AI, mobile technology, spatial computing and content creation.');
  return (
    <>
      <PageHero
        index="04"
        kicker="The setup"
        title={<>The tools are real.<br /><em>So is the standard.</em></>}
        copy="I enjoy beautiful, overpowered technology. This is not every cable or spare device — it is the edited set that says something about how I work, test and make."
      >
        <div className="field-stats"><span>{hardwareItems.length} tools</span><span>Current / curated</span></div>
      </PageHero>

      <section className="hardware-gallery section-pad section-rule">
        <div className="hardware-intro">
          <div>
            <p className="kicker">Current constellation</p>
            <h2>Curated by role,<br /><em>not by price tag.</em></h2>
          </div>
          <p>Each item earns its place by doing a different job: local compute, portable work, a wider canvas, a second ecosystem, clean capture or tactile control.</p>
        </div>

        <div className="hardware-grid">
          {[...hardwareItems].sort((a, b) => Number(Boolean(b.foundation)) - Number(Boolean(a.foundation))).map((item, index) => (
            <article className={`hardware-card ${item.size === 'wide' ? 'is-wide' : ''} ${item.fit === 'lg' ? 'is-lg-card' : ''}`} key={item.name}>
              <div className={`hardware-media tone-${item.tone}`}>
                <span className="hardware-number">H / {String(index + 1).padStart(2, '0')}</span>
                <span className="hardware-state">{item.foundation ? 'Foundation' : 'In the setup'}</span>
                {item.image ? (
                  <img
                    src={item.image}
                    alt={`${item.name} product view`}
                    loading="lazy"
                    className={item.fit === 'contain' ? 'is-contain' : item.fit === 'lg' ? 'is-contain is-lg-monitor' : ''}
                  />
                ) : (
                  <div className="maker-visual" aria-hidden="true">
                    <div className="maker-cube"><span /><span /><span /></div>
                    <Box />
                    <small>MODEL / OPEN</small>
                  </div>
                )}
              </div>
              <div className="hardware-body">
                <div className="hardware-meta"><span>{item.category}</span><span>{item.label}</span></div>
                <h3>{item.name}</h3>
                <p>{item.copy}</p>
                {item.sources.length > 0 && (
                  <div className="hardware-sources" aria-label={`Official sources for ${item.name}`}>
                    {item.sources.map(([label, url]) => (
                      <a href={url} target="_blank" rel="noreferrer" key={url}>{label} <ExternalLink size={10} /></a>
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>

        <p className="image-credit">
          Product imagery © the respective manufacturers. Sony α7 II photo: Roman Eisele / Wikimedia Commons,
          <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noreferrer"> CC BY-SA 4.0</a> (resized).
        </p>
      </section>

      <section className="setup-philosophy section-pad section-rule">
        <div><p className="kicker">The anti-haul</p><h2>The best device is the one that changes the workflow.</h2></div>
        <div>
          {[
            'Use both ecosystems before choosing a side',
            'Make the existing stack earn the next purchase',
            'Show product models; keep configuration details private',
            'Turn experiments into visible proof',
          ].map((item) => <p key={item}><Check size={17} />{item}</p>)}
        </div>
      </section>
    </>
  );
}

function Edc() {
  usePageMeta('EDC', 'The eleven confirmed digital and analog tools Abood carries for mobile work, capture, power, organization and fast thinking.');
  return (
    <>
      <PageHero
        index="06"
        kicker="Everyday carry"
        title={<>What leaves<br /><em>the desk with me.</em></>}
        copy="Not a maximalist pocket dump. Eleven confirmed pieces that reduce friction between an idea, a working session, a trip and the moment something needs to be captured."
      >
        <div className="field-stats"><span>11 confirmed items</span><span>Digital + analog</span></div>
      </PageHero>

      <section className="edc-gallery section-pad section-rule">
        <div className="edc-intro">
          <div>
            <p className="kicker">The carry logic</p>
            <h2>Enough capability.<br /><em>No invented filler.</em></h2>
          </div>
          <p>Each piece has a public-safe, specific role. Exact models stay exact; the ordinary paper pad stays deliberately unbranded.</p>
        </div>

        <div className="edc-grid">
          {[...edcItems].sort((a, b) => Number(a.number) - Number(b.number)).map((item) => (
            <article className={`edc-card ${item.size === 'wide' ? 'is-wide' : ''} ${item.size === 'half' ? 'is-half' : ''}`} key={item.number}>
              <div className={`edc-media tone-${item.tone}`}>
                <span className="edc-number">EDC / {item.number}</span>
                <span className="edc-state">Confirmed</span>
                {item.image ? (
                  <img src={item.image} alt={`${item.name} product view`} loading="lazy" />
                ) : (
                  <div className="notepad-visual" aria-hidden="true">
                    <div className="notepad-sheet"><i /><i /><i /><i /><i /></div>
                    <NotebookPen />
                    <small>UNBRANDED / PAPER</small>
                  </div>
                )}
              </div>
              <div className="edc-body">
                <div className="edc-meta"><span>{item.category}</span><span>{item.label}</span></div>
                <h3>{item.name}</h3>
                <p>{item.copy}</p>
                {item.sources.length > 0 && (
                  <div className="hardware-sources" aria-label={`Sources for ${item.name}`}>
                    {item.sources.map(([label, url]) => (
                      <a href={url} target="_blank" rel="noreferrer" key={url}>{label} <ExternalLink size={10} /></a>
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="edc-principle section-pad section-rule">
        <p className="kicker">Carry principle</p>
        <blockquote>Carry the tools that remove friction.<br /><em>Leave the performance at home.</em></blockquote>
        <p>Portable computing, stable capture, enough power, clean organization—and one normal page for thoughts that should not open another app.</p>
      </section>
    </>
  );
}

function Stack() {
  usePageMeta('Stack', 'The seven tools Abood actually uses to organize work, preserve knowledge, build software, research, dictate and connect his machines.');
  return (
    <>
      <PageHero
        index="07"
        kicker="Stack"
        title={<>Tools that earn<br /><em>a permanent tab.</em></>}
        copy="Seven tools, seven distinct jobs. This is the working set I actually return to—not a directory of every app installed on a device."
      >
        <div className="field-stats"><span>07 tools</span><span>Current working set</span></div>
      </PageHero>

      <section className="stack-gallery section-pad section-rule">
        <div className="stack-intro">
          <div>
            <p className="kicker">The selection rule</p>
            <h2>Clear roles.<br /><em>No logo collection.</em></h2>
          </div>
          <p>Each card answers one question: what does this tool reliably do for the way I think, work, build or connect?</p>
        </div>

        <div className="stack-grid">
          {stackItems.map((item) => (
            <article className={`stack-card ${item.size === 'wide' ? 'is-wide' : ''} ${item.size === 'half' ? 'is-half' : ''}`} key={item.number}>
              <div className="stack-media">
                <span className="stack-number">STACK / {item.number}</span>
                <img className={item.logoClass === 'wordmark' ? 'is-wordmark' : ''} src={item.logo} alt={`${item.name} official identity`} loading="lazy" />
              </div>
              <div className="stack-body">
                <h3>{item.name}</h3>
                <p>{item.description}</p>
                <a href={item.source} target="_blank" rel="noreferrer">Official site <ExternalLink size={10} /></a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="stack-principle section-pad section-rule">
        <p className="kicker">Stack principle</p>
        <blockquote>A tool stays when its role<br /><em>is clearer than its hype.</em></blockquote>
        <p>The list can change. The standard does not: regular use, a specific job, and a visible improvement in how the work moves.</p>
      </section>
    </>
  );
}

function Now() {
  const state = useSiteState();
  usePageMeta('Now', 'What currently has Abood Akkad’s attention: useful AI, finished proof, spatial computing and honest bilingual technology notes.');
  return (
    <>
      <PageHero
        index="05"
        kicker="Now / August 2026"
        title={<>What has my<br /><em>attention right now.</em></>}
        copy="A /now page is a public snapshot, not a permanent identity. This is where the curiosity is currently pointed."
      />
      <section className="now-board section-pad section-rule">
        <div className="now-signal">
          <div className="now-radar"><span /><span /><span /><i /></div>
          <p>Signal acquired</p>
          <time>Updated {formatDate(state.updatedAt)}</time>
        </div>
        <div className="now-main">
          <p className="kicker">Current question</p>
          <h2>{state.now.headline}</h2>
          <div className="now-threads">
            {state.now.threads.map((thread, index) => <article key={thread}><span>0{index + 1}</span><p>{thread}</p></article>)}
          </div>
        </div>
      </section>
      <section className="now-columns section-pad section-rule">
        <article><p className="kicker">Learning</p><h3>AI systems from the inside out</h3><p>Moving from fluency with tools toward better product judgement: specifications, evaluation, deployment and the unglamorous parts that make a build reliable.</p></article>
        <article><p className="kicker">Making</p><h3>Small, finished proof</h3><p>Turning research and strong opinions into working pages, useful workflows, experiments and explanations that another person can actually try.</p></article>
        <article><p className="kicker">Publishing</p><h3>Finding the honest format</h3><p>Bilingual field notes about AI and gadgets — less generic creator polish, more face, proof, real screens and a specific point of view.</p></article>
      </section>
    </>
  );
}

function NoteDetail() {
  const { slug } = useParams();
  const state = useSiteState();
  const note = useMemo(() => state.notes.find((item) => item.slug === slug), [state.notes, slug]);
  usePageMeta(note?.title || 'Field note not found', note?.dek || 'This field note does not exist.', note?.image);
  if (!note) return <NotFound compact />;

  const detailCopy = {
    'understanding-ai-not-vocabulary': {
      arabic: 'أوقات منكون حافظين المصطلحات قبل ما نكون فاهمين الفكرة نفسها.',
      body: [
        'Agents, MCP, RAG and workflows became ordinary vocabulary remarkably fast. That is useful — shared language helps us move. But it also creates an easy illusion of understanding.',
        'The better test begins one layer down. What is connected to what? Where does context enter? What can fail silently? Which part takes an action, and which part checks whether that action was sensible?',
        'The point is not to gatekeep the language. It is to turn the language back into a map of the actual system. If I cannot explain the map simply, I probably need another pass.',
      ],
      takeaway: 'Fluency is not recall. Fluency is being able to trace the system and predict where it may fail.',
    },
    'ask-maps-one-good-question': {
      arabic: 'بدل ما افتح عشر محلات وقارن بيناتن، سؤال واحد عطاني الخيار المناسب.',
      body: [
        'This was a small test, which is why I liked it. I needed a highly rated coffee shop with Wi-Fi and easy access in a neighbourhood I would be visiting.',
        'Normally that means opening a map, checking several places, reading reviews for the hidden constraints and keeping a rough comparison in my head. Ask Maps compressed that into one well-formed question.',
        'The value was not that AI discovered coffee. It was that the interface let me express the whole job at once. That is the kind of AI feature I keep: one that removes comparison friction without removing my judgement.',
      ],
      takeaway: 'A useful assistant does not just answer faster. It lets you describe the real decision in one place.',
    },
    'mirror-logic-test': {
      arabic: 'الاختبار الحقيقي مو الصورة الحلوة — الاختبار إذا فهم المنطق جوّاها.',
      body: [
        'Generative image comparisons often reward surface polish: cleaner skin, dramatic light, an impressive transformation. I was more interested in a smaller detail — the mirror.',
        'If a model changes the subject, can it also change the reflected body consistently? Reflections, shadows, hands and text are useful because they expose whether the scene still behaves as one scene.',
        'The result was visually strong, but the larger lesson is the test itself. Look for the dependency that a pretty output can hide. That is usually where the interesting failure lives.',
      ],
      takeaway: 'Judge generative media by the logic it preserves, not only the pixels it improves.',
    },
    'morning-briefing-not-doomscrolling': {
      arabic: 'نفس الموبايل، بس بداية أهدى وأفيد.',
      body: [
        'The first interaction of the morning is often automatic: reach for the phone, open a feed and accept whatever mixture of urgency and entertainment arrives.',
        'I asked my AI agent to prepare a short morning briefing instead — news and topics I genuinely care about, written in a format I like. It uses the same device and takes the same gesture, but changes what the gesture leads to.',
        'The experiment is not about perfect productivity. It is about replacing an algorithmic first choice with an intentional one. The system earns its place only if the briefing stays concise, useful and easier to return to than the feed.',
      ],
      takeaway: 'Good personal AI changes defaults. It does not ask for a heroic new habit every morning.',
    },
  }[note.slug];

  return (
    <article className="note-detail">
      <header className="note-detail-head section-pad">
        <Link className="back-link" to="/field-notes"><ArrowLeft size={15} /> Field notes</Link>
        <div className="note-detail-meta"><span>{note.tag}</span><time>{formatDate(note.date)}</time></div>
        <h1>{note.title}</h1>
        <p>{note.dek}</p>
      </header>
      <div className="note-hero-image section-pad"><img src={note.image} alt="Visual from the original field note" /></div>
      <div className="article-grid section-pad">
        <aside><span>FIELD / {note.date.slice(5).replace('-', '.')}</span><p>Personal observation<br />Dubai, UAE</p></aside>
        <div className="article-body">
          <blockquote lang="ar" dir="rtl">{detailCopy.arabic}</blockquote>
          {detailCopy.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          <div className="takeaway"><span>Working takeaway</span><p>{detailCopy.takeaway}</p></div>
          <a className="button button-outline" href={note.xUrl} target="_blank" rel="noreferrer">View the original post on X <ExternalLink size={15} /></a>
        </div>
      </div>
    </article>
  );
}

function NotFound({ compact = false }) {
  usePageMeta('404 — Signal lost', 'This page does not exist. Return to Abood Akkad’s personal site.');
  return (
    <section className={`not-found section-pad ${compact ? 'compact' : ''}`}>
      <span>404 / signal lost</span><h1>This trail<br /><em>goes nowhere yet.</em></h1><Link className="button button-solid" to="/">Return home <ArrowRight size={16} /></Link>
    </section>
  );
}

export default function App() {
  return (
    <PageShell>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/field-notes" element={<FieldNotes />} />
        <Route path="/experiments" element={<Experiments />} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/edc" element={<Edc />} />
        <Route path="/stack" element={<Stack />} />
        <Route path="/now" element={<Now />} />
        <Route path="/note/:slug" element={<NoteDetail />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </PageShell>
  );
}
